/**
 * F-148 draft editor reducer (design §3.2, §4.7, §4.8, §10.2).
 *
 * Pure: every action returns a new state. Ids (client line ids, capture ids) travel inside the
 * actions so the reducer never touches `crypto` and tests stay deterministic. Quantity edits go
 * through the shared `applyQuantityEdit` so the persisted `acceptedAuto` provenance is never
 * inferred from arithmetic. Scanning goes through the shared `applyReceivingScan` reducer.
 *
 * Server resolution (variant snapshot, candidates, issues) lives on each line and is refreshed by
 * `hydrate`. When the merchant has unsaved edits, `hydrate` merges resolution only and keeps the
 * local values and version; otherwise it replaces the whole state from the server.
 */
import {
  applyQuantityEdit,
  applyReceivingScan,
  consolidateLines,
  setPackConfirmation,
  validateDraftLines,
  type ReceivingDraft,
  type ReceivingDraftHeaderInput,
  type ReceivingDraftIssue,
  type ReceivingDraftIssueCode,
  type ReceivingDraftLine,
  type ReceivingDraftLineInput,
  type ReceivingDraftStatus,
  type ReceivingIssue,
  type ReceivingLineIssue,
  type ReceivingMatchMethod,
  type ReceivingQuantityField,
  type ReceivingReferenceView,
  type ReceivingScanLookup,
  type ReceivingScanOutcome,
  type ReceivingSupplier,
  type ReceivingUnitKind,
  type ReceivingVariantSnapshot,
} from '@dukanti/shared/admin';

export type EditorLine = ReceivingDraftLine & {
  /**
   * True after a local identifier/variant edit until the server re-resolves the line. While
   * stale and unresolved, identifier issues are hidden and the card shows a "checking" state.
   */
  resolutionStale: boolean;
};

export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'failed' | 'conflict';

export type IdentifierField = 'barcode' | 'variantRef' | 'supplierCode' | 'supplierItemName';

export interface ScanFeedback {
  seq: number;
  outcome: ReceivingScanOutcome;
  clientLineId: string | null;
}

export interface DraftEditorState {
  draftId: string;
  storeId: string;
  status: ReceivingDraftStatus;
  /** Last version the client knows to be persisted (used as `expectedVersion`). */
  version: number;
  header: ReceivingDraftHeaderInput;
  /** Display name for the selected supplier (server name or the combobox choice). */
  supplierName: string;
  supplier: ReceivingSupplier;
  reference: ReceivingReferenceView;
  postedReceiptId: string | null;
  serverIssues: ReceivingIssue<ReceivingDraftIssueCode>[];
  lines: EditorLine[];
  appliedCaptureIds: string[];
  dirty: boolean;
  saveStatus: SaveStatus;
  saveError: unknown;
  conflictVersion: number | null;
  /** Token + fingerprint of the payload last handed to a save; reused for same-payload retries. */
  pendingSave: { token: string; fingerprint: string } | null;
  lastScan: ScanFeedback | null;
  highlight: { clientLineId: string; seq: number } | null;
}

export type DraftEditorAction =
  | { type: 'hydrate'; draft: ReceivingDraft; force?: boolean }
  | { type: 'setHeader'; patch: Partial<ReceivingDraftHeaderInput>; supplierName?: string }
  | { type: 'addManualLine'; clientLineId: string }
  | { type: 'removeLine'; clientLineId: string }
  | {
      type: 'editQuantity';
      clientLineId: string;
      field: ReceivingQuantityField;
      value: number | null;
    }
  | { type: 'setUnitKind'; clientLineId: string; unitKind: ReceivingUnitKind }
  | { type: 'confirmPack'; clientLineId: string; confirmed: boolean }
  | {
      type: 'setVariant';
      clientLineId: string;
      variant: ReceivingVariantSnapshot;
      matchMethod: ReceivingMatchMethod;
    }
  | { type: 'clearVariant'; clientLineId: string }
  | { type: 'setIdentifier'; clientLineId: string; field: IdentifierField; value: string | null }
  | { type: 'setNote'; clientLineId: string; note: string | null }
  | { type: 'applyScan'; lookup: ReceivingScanLookup; captureId: string; newClientLineId: string }
  | { type: 'consolidate'; variantId: string }
  | { type: 'saveStarted'; token: string; fingerprint: string }
  | { type: 'saveSucceeded'; token: string; version: number }
  | {
      type: 'saveFailed';
      token: string;
      error: unknown;
      conflict: boolean;
      conflictVersion: number | null;
    }
  /** Server version moved forward by a non-save write (CSV import). */
  | { type: 'versionAdvanced'; version: number }
  | { type: 'highlight'; clientLineId: string }
  | { type: 'clearHighlight' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toLineInput(line: ReceivingDraftLineInput): ReceivingDraftLineInput {
  return {
    clientLineId: line.clientLineId,
    source: line.source,
    sourceRowNumber: line.sourceRowNumber,
    identifiers: {
      barcode: line.identifiers.barcode,
      variantRef: line.identifiers.variantRef,
      supplierCode: line.identifiers.supplierCode,
      supplierItemName: line.identifiers.supplierItemName,
    },
    variantId: line.variantId,
    matchMethod: line.matchMethod,
    unitKind: line.unitKind,
    packsExpected: line.packsExpected,
    unitsPerPack: line.unitsPerPack,
    unitsPerPackConfirmed: line.unitsPerPackConfirmed,
    expectedUnits: line.expectedUnits,
    acceptedUnits: line.acceptedUnits,
    damagedUnits: line.damagedUnits,
    missingUnits: line.missingUnits,
    note: line.note,
    acceptedAuto: line.acceptedAuto,
  };
}

/** Wraps a plain input as an editor line without server resolution. */
export function toEditorLine(
  input: ReceivingDraftLineInput,
  lineNo: number,
  variant: ReceivingVariantSnapshot | null = null,
): EditorLine {
  return {
    ...toLineInput(input),
    lineNo,
    variant,
    candidates: { byBarcode: [], byVariantRef: null },
    issues: [],
    resolutionStale:
      variant === null &&
      (input.identifiers.barcode !== null || input.identifiers.variantRef !== null),
  };
}

export function newManualLineInput(clientLineId: string): ReceivingDraftLineInput {
  return {
    clientLineId,
    source: 'manual',
    sourceRowNumber: null,
    identifiers: { barcode: null, variantRef: null, supplierCode: null, supplierItemName: null },
    variantId: null,
    matchMethod: null,
    unitKind: 'unit',
    packsExpected: null,
    unitsPerPack: null,
    unitsPerPackConfirmed: null,
    expectedUnits: null,
    acceptedUnits: null,
    damagedUnits: null,
    missingUnits: null,
    note: null,
    acceptedAuto: true,
  };
}

function renumber(lines: readonly EditorLine[]): EditorLine[] {
  return lines.map((line, index) =>
    line.lineNo === index + 1 ? line : { ...line, lineNo: index + 1 },
  );
}

function fromServerLine(line: ReceivingDraftLine): EditorLine {
  return { ...line, resolutionStale: false };
}

function headerFromDraft(draft: ReceivingDraft): ReceivingDraftHeaderInput {
  return {
    supplier: { kind: 'existing', supplierId: draft.supplier.id },
    shipmentReference: draft.shipmentReference,
    linkedReceiptId: draft.linkedReceiptId,
    note: draft.note,
  };
}

function referenceFromDraft(draft: ReceivingDraft): ReceivingReferenceView {
  return {
    referenceOrigin: draft.referenceOrigin,
    shipmentReference: draft.shipmentReference,
    systemReference: draft.systemReference,
    displayReference: draft.displayReference,
  };
}

/** True when any quantity holds the invalid-text sentinel; such payloads are never sent (§3.2). */
export function hasUnsendableValues(lines: readonly ReceivingDraftLineInput[]): boolean {
  return lines.some(
    (line) =>
      (line.expectedUnits !== null && line.expectedUnits < 0) ||
      (line.acceptedUnits !== null && line.acceptedUnits < 0) ||
      (line.damagedUnits !== null && line.damagedUnits < 0) ||
      (line.missingUnits !== null && line.missingUnits < 0) ||
      (line.packsExpected !== null && line.packsExpected < 0) ||
      (line.unitsPerPack !== null && line.unitsPerPack < 0),
  );
}

/** Stable JSON of the save payload; used to decide whether a retry may reuse its token. */
export function savePayloadFingerprint(
  header: ReceivingDraftHeaderInput,
  lines: readonly ReceivingDraftLineInput[],
): string {
  return JSON.stringify({ header, lines: lines.map(toLineInput) });
}

function touched(state: DraftEditorState): DraftEditorState {
  return {
    ...state,
    dirty: true,
    saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'unsaved',
  };
}

function updateLine(
  state: DraftEditorState,
  clientLineId: string,
  update: (line: EditorLine) => EditorLine,
): DraftEditorState {
  let changed = false;
  const lines = state.lines.map((line) => {
    if (line.clientLineId !== clientLineId) return line;
    changed = true;
    return update(line);
  });
  if (!changed) return state;
  return touched({ ...state, lines });
}

function lineIdAt(lines: readonly EditorLine[], lineNo: number): string | null {
  return lines[lineNo - 1]?.clientLineId ?? null;
}

function highlightFor(lines: readonly EditorLine[], outcome: ReceivingScanOutcome): string | null {
  switch (outcome.kind) {
    case 'added':
    case 'incremented':
    case 'pack_line':
    case 'unresolved_added':
    case 'already_unresolved':
    case 'quantity_limit':
      return lineIdAt(lines, outcome.lineNo);
    case 'duplicate_lines': {
      const firstLineNo = outcome.lineNos[0];
      return firstLineNo === undefined ? null : lineIdAt(lines, firstLineNo);
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export function initialDraftEditorState(draft: ReceivingDraft): DraftEditorState {
  return {
    draftId: draft.id,
    storeId: draft.storeId,
    status: draft.status,
    version: draft.version,
    header: headerFromDraft(draft),
    supplierName: draft.supplier.name,
    supplier: draft.supplier,
    reference: referenceFromDraft(draft),
    postedReceiptId: draft.postedReceiptId,
    serverIssues: draft.issues,
    lines: renumber(draft.lines.map(fromServerLine)),
    appliedCaptureIds: [],
    dirty: false,
    saveStatus: 'saved',
    saveError: null,
    conflictVersion: null,
    pendingSave: null,
    lastScan: null,
    highlight: null,
  };
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function draftEditorReducer(
  state: DraftEditorState,
  action: DraftEditorAction,
): DraftEditorState {
  switch (action.type) {
    case 'hydrate': {
      const { draft } = action;
      const busy = state.dirty || state.saveStatus === 'saving';
      if (action.force || !busy) {
        const fresh = initialDraftEditorState(draft);
        return {
          ...fresh,
          appliedCaptureIds: state.appliedCaptureIds,
          lastScan: state.lastScan,
          highlight: state.highlight,
        };
      }
      // Merge server resolution into local lines; keep local values and version.
      const byId = new Map(draft.lines.map((line) => [line.clientLineId, line]));
      const lines = state.lines.map((line) => {
        const server = byId.get(line.clientLineId);
        if (!server) return line;
        const sameIdentity =
          server.variantId === line.variantId &&
          server.identifiers.barcode === line.identifiers.barcode &&
          server.identifiers.variantRef === line.identifiers.variantRef;
        if (!sameIdentity) return line;
        return {
          ...line,
          variant: server.variant,
          candidates: server.candidates,
          issues: server.issues,
          resolutionStale: false,
        };
      });
      return {
        ...state,
        status: draft.status,
        postedReceiptId: draft.postedReceiptId,
        supplier: draft.supplier,
        reference: referenceFromDraft(draft),
        serverIssues: draft.issues,
        lines,
      };
    }

    case 'setHeader': {
      return touched({
        ...state,
        header: { ...state.header, ...action.patch },
        supplierName: action.supplierName ?? state.supplierName,
      });
    }

    case 'addManualLine': {
      const line = toEditorLine(newManualLineInput(action.clientLineId), state.lines.length + 1);
      return touched({
        ...state,
        lines: [...state.lines, line],
        highlight: { clientLineId: line.clientLineId, seq: (state.highlight?.seq ?? 0) + 1 },
      });
    }

    case 'removeLine': {
      const lines = state.lines.filter((line) => line.clientLineId !== action.clientLineId);
      if (lines.length === state.lines.length) return state;
      return touched({ ...state, lines: renumber(lines) });
    }

    case 'editQuantity':
      return updateLine(state, action.clientLineId, (line) =>
        applyQuantityEdit(line, action.field, action.value),
      );

    case 'setUnitKind':
      return updateLine(state, action.clientLineId, (line) => {
        if (line.unitKind === action.unitKind) return line;
        if (action.unitKind === 'pack') {
          const next: EditorLine = {
            ...line,
            unitKind: 'pack',
            packsExpected: null,
            unitsPerPack: null,
            unitsPerPackConfirmed: null,
          };
          // Expected is derived for pack lines; clearing it follows the §4.8 expected-edit rule.
          return applyQuantityEdit(next, 'expectedUnits', null);
        }
        return {
          ...line,
          unitKind: 'unit',
          packsExpected: null,
          unitsPerPack: null,
          unitsPerPackConfirmed: null,
        };
      });

    case 'confirmPack':
      return updateLine(state, action.clientLineId, (line) =>
        setPackConfirmation(line, action.confirmed),
      );

    case 'setVariant':
      return updateLine(state, action.clientLineId, (line) => ({
        ...line,
        variantId: action.variant.variantId,
        matchMethod: action.matchMethod,
        variant: action.variant,
        issues: [],
        resolutionStale: false,
      }));

    case 'clearVariant':
      return updateLine(state, action.clientLineId, (line) => ({
        ...line,
        variantId: null,
        matchMethod: null,
        variant: null,
        issues: [],
        resolutionStale: line.identifiers.barcode !== null || line.identifiers.variantRef !== null,
      }));

    case 'setIdentifier':
      return updateLine(state, action.clientLineId, (line) => {
        const value = action.value === '' ? null : action.value;
        const identifiers = { ...line.identifiers, [action.field]: value };
        const affectsResolution = action.field === 'barcode' || action.field === 'variantRef';
        return {
          ...line,
          identifiers,
          candidates: affectsResolution ? { byBarcode: [], byVariantRef: null } : line.candidates,
          issues: affectsResolution ? [] : line.issues,
          resolutionStale: affectsResolution ? line.variantId === null : line.resolutionStale,
        };
      });

    case 'setNote':
      return updateLine(state, action.clientLineId, (line) => ({
        ...line,
        note: action.note === '' ? null : action.note,
      }));

    case 'applyScan': {
      const result = applyReceivingScan<EditorLine>(
        { lines: state.lines, appliedCaptureIds: state.appliedCaptureIds },
        action.lookup,
        action.captureId,
        {
          createClientLineId: () => action.newClientLineId,
          createLine: (input) => {
            const variant =
              action.lookup.status === 'known'
                ? snapshotFromLookup(action.lookup, action.lookup.variant)
                : null;
            return toEditorLine(input, state.lines.length + 1, variant);
          },
        },
      );
      if (result.outcome.kind === 'ignored_repeat') {
        return state;
      }
      const changed = result.state.lines !== state.lines;
      const lines = changed ? renumber(result.state.lines) : state.lines;
      const clientLineId = highlightFor(lines, result.outcome);
      const next: DraftEditorState = {
        ...state,
        lines,
        appliedCaptureIds: [...result.state.appliedCaptureIds],
        lastScan: {
          seq: (state.lastScan?.seq ?? 0) + 1,
          outcome: result.outcome,
          clientLineId,
        },
        highlight: clientLineId
          ? { clientLineId, seq: (state.highlight?.seq ?? 0) + 1 }
          : state.highlight,
      };
      return changed ? touched(next) : next;
    }

    case 'consolidate': {
      const { lines, combined } = consolidateLines(state.lines, action.variantId);
      if (!combined) return state;
      return touched({
        ...state,
        lines: renumber(lines),
        highlight: { clientLineId: combined.clientLineId, seq: (state.highlight?.seq ?? 0) + 1 },
      });
    }

    case 'saveStarted':
      return {
        ...state,
        dirty: false,
        saveStatus: 'saving',
        saveError: null,
        pendingSave: { token: action.token, fingerprint: action.fingerprint },
      };

    case 'saveSucceeded': {
      if (state.pendingSave?.token !== action.token) {
        // A save this editor no longer tracks still moved the server forward: record the version
        // so no later save sends a stale `expectedVersion` (a false "changed in another window").
        return action.version > state.version ? { ...state, version: action.version } : state;
      }
      return {
        ...state,
        version: Math.max(state.version, action.version),
        saveStatus: state.dirty ? 'unsaved' : 'saved',
        saveError: null,
        conflictVersion: null,
        pendingSave: null,
      };
    }

    case 'saveFailed': {
      if (state.pendingSave?.token !== action.token) return state;
      const conflict = action.conflict;
      return {
        ...state,
        // The payload is still unsaved; keep it dirty so a retry or autosave sends it again.
        dirty: true,
        saveStatus: conflict ? 'conflict' : 'failed',
        saveError: action.error,
        conflictVersion: action.conflictVersion,
        // Keep the token so a retry of the identical payload reuses it (§2.3).
        pendingSave: conflict ? null : state.pendingSave,
      };
    }

    case 'versionAdvanced':
      return action.version > state.version ? { ...state, version: action.version } : state;

    case 'highlight':
      return {
        ...state,
        highlight: { clientLineId: action.clientLineId, seq: (state.highlight?.seq ?? 0) + 1 },
      };

    case 'clearHighlight':
      return state.highlight ? { ...state, highlight: null } : state;

    default:
      return state;
  }
}

function snapshotFromLookup(
  lookup: Extract<ReceivingScanLookup, { status: 'known' }>,
  variant: Extract<ReceivingScanLookup, { status: 'known' }>['variant'],
): ReceivingVariantSnapshot {
  return {
    variantId: variant.variantId,
    productId: variant.productId,
    productName: variant.productName,
    optionSummary: variant.variantOptionSummary,
    barcode: lookup.normalized,
    imagePath: variant.primaryImagePath,
    inventory: variant.inventory,
    active: variant.active,
    eligible: true,
  };
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const IDENTIFIER_ISSUE_CODES: ReadonlySet<string> = new Set([
  'VARIANT_REQUIRED',
  'IDENTIFIER_UNKNOWN',
  'IDENTIFIER_AMBIGUOUS',
  'IDENTIFIER_CONFLICT',
  'VARIANT_INELIGIBLE',
]);

export interface DraftIssues {
  draftIssues: ReceivingDraftIssue[];
  /** Line issues keyed by clientLineId (client feedback; the server recomputes at post). */
  byLine: Map<string, ReceivingLineIssue[]>;
  blockingCount: number;
  /** Lines whose identifiers are waiting for server resolution. */
  checkingLineIds: ReadonlySet<string>;
}

export function selectDraftIssues(state: DraftEditorState): DraftIssues {
  const { draftIssues, lineIssues } = validateDraftLines(state.lines);
  const byLine = new Map<string, ReceivingLineIssue[]>();
  const checking = new Set<string>();
  for (const line of state.lines) byLine.set(line.clientLineId, []);
  for (const issue of lineIssues) {
    const line = issue.lineNo ? state.lines[issue.lineNo - 1] : undefined;
    if (!line) continue;
    if (line.resolutionStale && line.variantId === null && IDENTIFIER_ISSUE_CODES.has(issue.code)) {
      checking.add(line.clientLineId);
      continue;
    }
    byLine.get(line.clientLineId)?.push(issue);
  }
  const serverBlocking = state.serverIssues.filter(
    (issue) =>
      issue.severity === 'blocking' && issue.code !== 'NO_LINES' && issue.code !== 'TOO_MANY_LINES',
  );
  const allDraftIssues: ReceivingDraftIssue[] = [...draftIssues, ...serverBlocking];
  let blockingCount = allDraftIssues.filter((i) => i.severity === 'blocking').length;
  for (const issues of byLine.values()) {
    blockingCount += issues.filter((i) => i.severity === 'blocking').length;
  }
  return { draftIssues: allDraftIssues, byLine, blockingCount, checkingLineIds: checking };
}

export interface DuplicateGroup {
  variantId: string;
  productName: string;
  optionSummary: string;
  lineNos: number[];
  preview: {
    expected: number | null;
    accepted: number | null;
    damaged: number | null;
    missing: number | null;
  };
}

export function selectDuplicateGroups(state: DraftEditorState): DuplicateGroup[] {
  const groups = new Map<string, EditorLine[]>();
  for (const line of state.lines) {
    if (line.variantId === null) continue;
    const list = groups.get(line.variantId);
    if (list) list.push(line);
    else groups.set(line.variantId, [line]);
  }
  const out: DuplicateGroup[] = [];
  for (const [variantId, lines] of groups) {
    if (lines.length < 2) continue;
    const { combined } = consolidateLines(state.lines, variantId);
    const first = lines[0];
    if (!first) continue;
    out.push({
      variantId,
      productName: first.variant?.productName ?? '',
      optionSummary: first.variant?.optionSummary ?? '',
      lineNos: lines.map((l) => l.lineNo),
      preview: {
        expected: combined?.expectedUnits ?? null,
        accepted: combined?.acceptedUnits ?? null,
        damaged: combined?.damagedUnits ?? null,
        missing: combined?.missingUnits ?? null,
      },
    });
  }
  return out;
}

export function selectLineInputs(state: DraftEditorState): ReceivingDraftLineInput[] {
  return state.lines.map(toLineInput);
}
